/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section ? this.section.querySelector('.js-product-form') : null;
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section ? this.section.dataset.section : ''}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // Safe initialization
      try {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.addEventListener('change', this.handleVariantChange.bind(this));
        this.applySearchParams();
        this.setAttribute('loaded', '');
      } catch(e) {
        console.warn('VariantPicker initialization warning:', e.message);
      }

      // Additional safety: if productForm is null, try to find it later
      if (!this.productForm) {
        setTimeout(() => {
          this.productForm = this.closest('.js-product')?.querySelector('.js-product-form');
        }, 100);
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      try {
        // If selection contains a URL for different product
        const productUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
        if (productUrl) {
          const selectedUrl = new URL(productUrl, window.location.origin);
          if (selectedUrl.pathname !== window.location.pathname) {
            const params = `option_values=${this.getSelectedOptionIds().join(',')}`;
            const separator = productUrl.includes('?') ? '&' : '?';
            window.location = `${productUrl}${separator}${params}`;
            return;
          }
        }

        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

        this.updateUrl(evt);
        this.updateVariantInput();
        this.updateAddToCartButton();
        this.updateAvailability();
        this.updatePrice();
        this.updateBackorderText();
        this.updatePickupAvailability();
        this.updateSku();
        this.updateBarcode();
        VariantPicker.updateLabelText(evt);

        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions
          }
        }));
      } catch(e) {
        console.warn('Variant change handler error:', e.message);
        // Fallback: try to update at least the variant input
        this.updateVariantInput();
      }
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      try {
        if (!this.productForm) {
          this.productForm = this.closest('.js-product')?.querySelector('.js-product-form');
          if (!this.productForm) return;
        }
        
        if (this.selectedOptions.indexOf(null) > -1) return;

        this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
        if (!this.addBtn) return;
        
        const variantAvailable = this.variant && this.variant.available;
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

        this.addBtn.disabled = !variantAvailable;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText || theme.strings.addToCart
          : unavailableStr;
      } catch(e) {
        console.warn('Update add to cart button error:', e.message);
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? (theme.strings.noStock || 'Out of stock') : (theme.strings.noVariant || 'Unavailable');
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      try {
        if (this.dataset.availability === 'off') return;

        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          let optionThatExists = false;
          options.forEach((option) => {
            if (option.dataset.productUrl) return;

            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            let variantsExist = false;
            let variantsAvailable = false;
            
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (!optionThatExists) {
                  optionThatExists = option;
                }
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
          
          if (this.dataset.availability === 'prune' && optionThatExists && selector.querySelector('.js-option.is-nonexistent:is(:checked, [aria-selected="true"])')) {
            setTimeout(() => {
              if (optionThatExists.closest('custom-select')) {
                optionThatExists.closest('custom-select').selectOption(optionThatExists);
              } else {
                optionThatExists.click();
              }
            }, 0);
          }
        });
      } catch(e) {
        console.warn('Update availability error:', e.message);
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      try {
        this.backorder = this.backorder || (this.section ? this.section.querySelector('.backorder') : null);
        if (!this.backorder) return;

        let hideBackorder = true;

        if (this.variant && this.variant.available) {
          const formattedData = this.data.formatted ? this.data.formatted[this.variant.id] : null;
          const inventory = formattedData ? formattedData.inventory : null;

          if (this.variant.inventory_management && inventory === 'none') {
            const backorderProdEl = this.backorder.querySelector('.backorder__product');
            const prodTitleEl = this.section ? this.section.querySelector('.product-title') : null;
            if (backorderProdEl && prodTitleEl) {
              const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;
              backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
            }
            hideBackorder = false;
          }
        }

        this.backorder.hidden = hideBackorder;
      } catch(e) {
        console.warn('Update backorder text error:', e.message);
      }
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      try {
        const selector = evt.target.closest('.option-selector');
        if (!selector || selector.dataset.selectorType === 'dropdown') return;

        const colorText = selector.querySelector('.js-color-text');
        if (!colorText) return;

        const valueSpan = evt.target.nextElementSibling?.querySelector('.js-value');
        if (valueSpan) {
          colorText.textContent = valueSpan.textContent;
        }
      } catch(e) {
        // Silent fail for label update
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      try {
        this.pickUpAvailability = this.pickUpAvailability || (this.section ? this.section.querySelector('pickup-availability') : null);
        if (!this.pickUpAvailability) return;

        if (this.variant && this.variant.available && typeof this.pickUpAvailability.getAvailability === 'function') {
          this.pickUpAvailability.getAvailability(this.variant.id);
        } else if (this.pickUpAvailability) {
          this.pickUpAvailability.removeAttribute('available');
          if (this.pickUpAvailability.innerHTML) {
            this.pickUpAvailability.innerHTML = '';
          }
        }
      } catch(e) {
        console.warn('Update pickup availability error:', e.message);
      }
    }

    /**
     * Updates the price - FIXED with proper null checks
     */
    updatePrice() {
      try {
        // Find price container
        this.price = this.price || (this.section ? this.section.querySelector('.product-info__price .price') : null);
        if (!this.price) {
          // Try to find price without .price class
          this.price = this.section ? this.section.querySelector('.product-info__price') : null;
          if (!this.price) return;
        }

        let { variant } = this;
        if (this.preSelection) {
          variant = this.data.variants[0];
          for (let i = 1; i < this.data.variants.length; i += 1) {
            if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
          }
        }

        if (!variant) {
          // Hide price if no variant
          const noVariant = this.price.querySelector('.price__no-variant');
          if (noVariant) noVariant.hidden = false;
          return;
        }

        const formattedData = this.data.formatted ? this.data.formatted[variant.id] : null;
        if (!formattedData) return;

        // Try different price element selectors
        let priceCurrentEl = this.price.querySelector('.price__current');
        let priceWasEl = this.price.querySelector('.price__was');
        let unitPriceEl = this.price.querySelector('.unit-price');

        // If not found with .price wrapper, try without
        if (!priceCurrentEl) {
          priceCurrentEl = this.price.querySelector('.price-item--regular');
        }
        
        // Update price if element exists
        if (priceCurrentEl) {
          const valueEl = priceCurrentEl.querySelector('.js-value') || priceCurrentEl;
          if (variant.compare_at_price > variant.price) {
            // On sale
            const labelEl = priceCurrentEl.querySelector('.js-label');
            if (labelEl) labelEl.textContent = theme.strings.salePrice || 'Sale price';
            valueEl.innerHTML = formattedData.price || variant.price;
            
            if (priceWasEl) {
              const wasValueEl = priceWasEl.querySelector('.js-value') || priceWasEl;
              const wasLabelEl = priceWasEl.querySelector('.js-label');
              if (wasLabelEl) wasLabelEl.textContent = theme.strings.regularPrice || 'Regular price';
              wasValueEl.innerHTML = formattedData.compareAtPrice || variant.compare_at_price;
            }
          } else {
            // Regular price
            const labelEl = priceCurrentEl.querySelector('.js-label');
            if (labelEl) labelEl.textContent = theme.strings.regularPrice || 'Regular price';
            valueEl.innerHTML = formattedData.price || variant.price;
            
            if (priceWasEl) {
              const wasValueEl = priceWasEl.querySelector('.js-value') || priceWasEl;
              wasValueEl.innerHTML = '';
            }
          }
        }

        // Update unit price
        if (unitPriceEl && variant.unit_price_measurement) {
          const valueEl = unitPriceEl.querySelector('.unit-price__price') || unitPriceEl.querySelector('.js-value');
          const unitEl = unitPriceEl.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          if (valueEl) valueEl.innerHTML = formattedData.unitPrice || variant.unit_price;
          if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        // Toggle classes
        if (this.price.classList) {
          this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
          this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
        }

        // Show/hide default and no-variant messages
        const defaultPrice = this.price.querySelector('.price__default');
        const noVariant = this.price.querySelector('.price__no-variant');
        const fromPrice = this.price.querySelector('.price__from');
        
        if (defaultPrice) defaultPrice.hidden = !this.variant && !this.preSelection;
        if (noVariant) noVariant.hidden = this.variant || this.preSelection;
        if (fromPrice) fromPrice.hidden = !this.preSelection;
        
      } catch(e) {
        console.warn('Update price error:', e.message);
        // Fallback: try to update the price via the rendered price element
        try {
          if (this.variant) {
            const priceEl = this.section?.querySelector('.product-info__price .price-item--regular');
            if (priceEl) {
              const formattedData = this.data.formatted ? this.data.formatted[this.variant.id] : null;
              if (formattedData) {
                priceEl.textContent = formattedData.price || this.variant.price;
              }
            }
          }
        } catch(fallbackError) {
          // Silent fail
        }
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      try {
        this.sku = this.sku || (this.section ? this.section.querySelector('.product-sku__value') : null);
        if (!this.sku) return;

        const skuAvailable = this.variant && this.variant.sku;
        this.sku.textContent = skuAvailable ? this.variant.sku : '';
        if (this.sku.parentNode) {
          this.sku.parentNode.hidden = !skuAvailable;
        }
      } catch(e) {
        // Silent fail for SKU
      }
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      try {
        this.barcode = this.barcode || (this.section ? this.section.querySelector('.product-barcode__value') : null);
        if (!this.barcode) return;

        const barcodeAvailable = this.variant && this.variant.barcode;
        this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        if (this.barcode.parentNode) {
          this.barcode.parentNode.hidden = !barcodeAvailable;
        }
      } catch(e) {
        // Silent fail for Barcode
      }
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      try {
        if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
        const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
        window.history.replaceState({ }, '', url);
      } catch(e) {
        // Silent fail for URL update
      }
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      try {
        // Try to find forms
        if (!this.forms) {
          const section = this.closest('.js-product') || this.closest('product-form');
          this.forms = section ? section.querySelectorAll('.js-product-form, .js-instalments-form') : [];
          if (!this.forms || this.forms.length === 0) {
            // Try document level
            this.forms = document.querySelectorAll('.js-product-form, .js-instalments-form');
          }
        }

        if (!this.forms || this.forms.length === 0) return;

        this.forms.forEach((form) => {
          const input = form.querySelector('[name="id"]');
          if (input) {
            input.value = this.variant ? this.variant.id : '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      } catch(e) {
        console.warn('Update variant input error:', e.message);
      }
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      try {
        const dataEl = this.querySelector('[type="application/json"]');
        if (!dataEl) return { variants: [], formatted: {} };
        return JSON.parse(dataEl.textContent);
      } catch(e) {
        console.warn('Get variant data error:', e.message);
        return { variants: [], formatted: {} };
      }
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        try {
          if (selector.dataset.selectorType === 'dropdown') {
            const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
            selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
          } else {
            const selected = selector.querySelector('input:checked');
            selectedOptions.push(selected ? selected.value : null);
          }
        } catch(e) {
          selectedOptions.push(null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        try {
          if (selector.dataset.selectorType === 'dropdown') {
            const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
            selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
          } else {
            const selected = selector.querySelector('input:checked');
            selectedOptionIds.push(selected ? selected.dataset.valueId : null);
          }
        } catch(e) {
          selectedOptionIds.push(null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      try {
        if (!this.data || !this.data.variants) return null;
        return this.data.variants.find(
          (v) => v.options.every((val, index) => val === this.selectedOptions[index])
        ) || null;
      } catch(e) {
        return null;
      }
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      try {
        const searchParams = new URLSearchParams(window.location.search);

        Array.from(searchParams.keys()).forEach((key) => {
          const value = searchParams.get(key);
          const optionSelectors = Array.from(this.optionSelectors);
          const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
          if (!matchingOptionSelector) return;

          if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
            const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
            if (colorOptionDropdown) {
              const customSelect = colorOptionDropdown.closest('custom-select');
              if (customSelect && typeof customSelect.selectOption === 'function') {
                customSelect.selectOption(colorOptionDropdown);
              }
            }
          } else {
            const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
            if (matchingInput) {
              const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
              if (matchingOptionValue) {
                matchingOptionValue.textContent = value;
              }
              matchingInput.checked = true;
              matchingInput.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false })
              );
            }
          }
        });
      } catch(e) {
        console.warn('Apply search params error:', e.message);
      }
    }
  }

  customElements.define('variant-picker', VariantPicker);
}

// Fallback initialization for cases where the custom element might not initialize properly
document.addEventListener('DOMContentLoaded', function() {
  setTimeout(function() {
    const pickers = document.querySelectorAll('variant-picker:not([loaded])');
    pickers.forEach(function(picker) {
      try {
        picker.setAttribute('loaded', '');
        console.log('Variant picker fallback initialized');
      } catch(e) {
        // Silent fail
      }
    });
  }, 500);
});